import { UiOption } from '@/lib/helpers';

import { ClassifierModel, ClassifierSubType } from '../classifier';
import { PolicyModelV1 } from './policy-v1.model';
import {
  ControlActionValue,
  ControlCategoryActionMapping,
  getControlActionLevel,
  getSettingOptions,
} from './controls';

export type ClassifierEntity = {
  action: ControlActionValue;
  classifierId: string;
};

export type ClassifierControlConfig = {
  entities: ClassifierEntity[];
};

export type RegexPatternConfig = {
  entities: ClassifierEntity[];
};

export type ClassifierControlSettings = {
  classifierId: string;
  label: string;
  description: string;
  actions: ControlActionValue[];
  defaultAction: ControlActionValue;
  enabled?: boolean;
};

export const ClassifierControlsSettings = (classifiers: ClassifierModel[]) => {
  return classifiers.map(
    ({ id, name, description, enabled, subtype }) =>
      ({
        classifierId: id,
        label: name,
        description,
        actions:
          subtype === ClassifierSubType.Regex
            ? ['ignore', 'log', 'warn', 'block', 'tag', 'mask', 'delete']
            : ['ignore', 'log', 'warn', 'block'],
        defaultAction: 'ignore',
        enabled,
      }) as ClassifierControlSettings
  );
};

export const getClassifierControlActionOptions = (
  classifiers: ClassifierModel[],
  policy: PolicyModelV1,
  orgPolicy?: PolicyModelV1,
  subtype: ClassifierSubType = ClassifierSubType.Custom
): Map<string, UiOption[]> => {
  const options: Map<string, UiOption[]> = new Map();

  const inheritsFromOrg = policy.inherits();

  ClassifierControlsSettings(classifiers).forEach(({ classifierId, actions, defaultAction }) => {
    let settingOptions: UiOption[] = [];

    ControlCategoryActionMapping.forEach(({ header, actions: categoryActions }) => {
      if (categoryActions.some((action) => actions.includes(action))) {
        settingOptions.push({
          label: header,
          value: header.toLowerCase(),
          meta: { header: true },
        });
        for (const action of categoryActions) {
          if (actions.includes(action)) {
            settingOptions.push(getSettingOptions(action, false));
          }
        }
      }
    });

    if (inheritsFromOrg) {
      settingOptions = settingOptions.map((setting) => {
        const { value, meta } = setting;

        if (meta?.header) {
          return setting;
        }

        const orgEntities =
          subtype === ClassifierSubType.Regex
            ? orgPolicy?.regexPatterns?.entities
            : orgPolicy?.classifierControls.entities;
        const orgControl = orgEntities?.find(
          ({ classifierId: orgClassifierId }) => orgClassifierId === classifierId
        );
        const orgAction = orgControl?.action || defaultAction;
        const orgActionLevel = getControlActionLevel(orgAction);
        const compLevel = getControlActionLevel(value as ControlActionValue);

        if (orgActionLevel === compLevel) {
          setting.meta = { orgPolicyLevel: true };
        } else if (orgActionLevel < compLevel) {
          setting.disabled = true;
          setting.meta = { blockedByOrgPolicy: true };
        }

        return setting;
      });
    }

    options.set(classifierId, settingOptions);
  });

  return options;
};
